import { useMemo, useContext, useCallback } from "react";
import React from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import type { NRErrorInfo } from "@swiggy-private/js-utils";
import { PLWidgetController, WidgetControllerContext } from "@swiggy-private/dls-react-mxn";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import {
    AnalyticsContext,
    ScreenNames,
    ObjectNames,
    TableContext,
    sendEvent,
    IApiConfigs,
    ResponseContext,
} from "@swiggy-private/portal-dineout-context";
import { useTheme } from "styled-components";
import { usePopupCards } from "../hooks/usePopupCards";
interface IPLWidgetRendererProps {
    cards: IPLWidgetControllerCards[];
    onInvalidWidget: (widgetName?: string) => void;
    onWidgetError: (error: NRErrorInfo, widgetName?: string) => void;
    onEmptyPage: () => void;
    popupConfigs?: IApiConfigs;
    screenName: ScreenNames;
}

const PLWidgetRenderer = ({
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    cards,
    popupConfigs,
    screenName,
}: IPLWidgetRendererProps) => {
    // Get parent context to preserve all values (sdk, headers, clickHandler, etc.)
    const parentContext = useContext(WidgetControllerContext);
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const { state } = useContext(ResponseContext);
    const { colorMode } = useTheme();
    // Get analytics and table context for creating onWidgetView callback
    const { impressionEvent } = useContext(AnalyticsContext);
    const onWidgetView = useCallback(
        (widgetData?: Record<string, unknown>) => {
            const entityId = widgetData?.entityId;
            if (!isEmpty(entityId)) {
                // Send analytics event
                sendEvent({
                    eventFn: impressionEvent,
                    screenName,
                    objectName: ObjectNames.IMPRESSION_POPUP_WIDGET,
                    context: JSON.stringify({
                        restaurant_id: restaurantId,
                        table_id: tableId,
                        entity_id: entityId,
                        parent_order_id: parentOrderId,
                        table_no: state?.pin?.data?.tableNo,
                        theme: colorMode,
                    }),
                });
            }
        },
        [
            impressionEvent,
            screenName,
            restaurantId,
            tableId,
            parentOrderId,
            state?.pin?.data?.tableNo,
            colorMode,
        ]
    );

    // Extract popup cards and get handleWidgetView with frequency capping logic
    const { popupCards, handleWidgetView } = usePopupCards({
        cards,
        configs: popupConfigs,
        tableId,
        onWidgetView,
    });

    // Combine regular cards with popup cards
    const finalCards = useMemo(() => {
        return popupCards.length > 0 ? [...cards, ...popupCards] : cards;
    }, [cards, popupCards]);
    // this is for the onWidgetView callback to be called when a popup is viewed
    // for now popups is only using this feature.
    // TODO: in funture we can move this to context when there is new requirement.
    const mergedContext = useMemo(
        () => ({
            ...parentContext,
            ...(popupConfigs && { viewHandler: handleWidgetView }),
        }),
        [parentContext, popupConfigs, handleWidgetView]
    );

    return (
        <WidgetControllerContext.Provider value={mergedContext}>
            <PLWidgetController
                cards={finalCards}
                onInvalidWidget={onInvalidWidget}
                onWidgetError={onWidgetError}
                onEmptyPage={onEmptyPage}
            />
        </WidgetControllerContext.Provider>
    );
};

export default PLWidgetRenderer;
